import { Fragment, useMemo, useState, useCallback } from "react"
import { DateTime } from "luxon"
import {
  EmailStats,
  ExitPopupConversionFunnel,
  LeadByPropertyAndResidency,
  LeadByStateAndCity,
  LeadConversionFunnel,
  LeadConversionProgress,
  LeadGenerationTrend,
  LeadList,
  RateTableUsageMetrics,
  SubscriptionCount,
  TopLeadBreakdown,
  UTMParameters,
  withLazyLoad
} from "../../common/ui"
import { useUserRoles } from "../../common/hooks"
import { RequireRole } from "../../common/validation"
import { ALLOWED_ROLES_BY_ROUTE } from "../../common/constants"
import { DateRange } from "../../types"
import DateFilter, {
  DateRangePreset
} from "../../common/ui/activity/date-filter"

const LazyLoadedEmailStats = withLazyLoad(EmailStats)
const LazyLoadedExitPopupConversionFunnel = withLazyLoad(
  ExitPopupConversionFunnel
)
const LazyLoadedLeadByPropertyAndResidency = withLazyLoad(
  LeadByPropertyAndResidency
)
const LazyLoadedLeadByStateAndCity = withLazyLoad(LeadByStateAndCity)
const LazyLoadedLeadConversionFunnel = withLazyLoad(LeadConversionFunnel)
const LazyLoadedLeadConversionProgress = withLazyLoad(LeadConversionProgress)
const LazyLoadedLeadGenerationTrend = withLazyLoad(LeadGenerationTrend)
const LazyLoadedLeadList = withLazyLoad(LeadList)
const LazyLoadedRateTableUsageMetrics = withLazyLoad(RateTableUsageMetrics)
const LazyLoadedSubscriptionCount = withLazyLoad(SubscriptionCount)
const LazyLoadedTopLeadBreakdown = withLazyLoad(TopLeadBreakdown)
const LazyLoadedUTMParameters = withLazyLoad(UTMParameters)

const Stats = () => {
  const { user, isBankAdmin, isLoanOfficer, isSuperAdmin } = useUserRoles()

  const [selectedDateRange, setSelectedDateRange] =
    useState<DateRangePreset>("last_30")
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null)
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null)

  const orgId = useMemo(() => {
    return !isSuperAdmin && isBankAdmin ? user?.org?.id : undefined
  }, [isBankAdmin, isSuperAdmin, user?.org?.id])
  const loid = useMemo(() => {
    return !isSuperAdmin && !isBankAdmin && isLoanOfficer ? user?.id : undefined
  }, [isBankAdmin, isLoanOfficer, isSuperAdmin, user?.id])

  // Convert DateRangePreset to days for stats components
  const range = useMemo<DateRange>(() => {
    switch (selectedDateRange) {
      case "today":
        return 1
      case "last_7":
        return 7
      case "last_30":
        return 30
      case "last_90":
        return 90
      case "custom":
        if (customStartDate && customEndDate) {
          const start = DateTime.fromJSDate(customStartDate)
          const end = DateTime.fromJSDate(customEndDate)
          return Math.ceil(end.diff(start, "days").days) as DateRange
        }
        return 30
      case "all":
      default:
        return 1000
    }
  }, [selectedDateRange, customStartDate, customEndDate])

  const handleDateRangeChange = useCallback(
    (range: DateRangePreset, startDate?: Date, endDate?: Date) => {
      setSelectedDateRange(range)
      if (range === "custom" && startDate && endDate) {
        setCustomStartDate(startDate)
        setCustomEndDate(endDate)
      }
    },
    []
  )

  const handleClearDateFilter = useCallback(() => {
    setSelectedDateRange("all")
    setCustomStartDate(null)
    setCustomEndDate(null)
  }, [])

  return (
    <RequireRole allowedRoles={ALLOWED_ROLES_BY_ROUTE.stats}>
      <Fragment>
        <div className="bg-white flex flex-row items-center justify-between px-6 py-4">
          <div className="text-2xl font-medium">Stats</div>
          <DateFilter
            selectedRange={selectedDateRange}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            onRangeChange={handleDateRangeChange}
            onClearFilter={handleClearDateFilter}
          />
        </div>
        <div
          id="stats-container"
          className="grid gap-6 grid-cols-12 p-6 pb-20 bg-primary-100"
        >
          {range && (
            <Fragment>
              <div className="col-span-12 lg:col-span-6 flex min-h-[450px]">
                <LazyLoadedLeadGenerationTrend
                  key={`lead-generation-trend-${range}`}
                  title="Lead Generation Trend"
                  range={range}
                  orgId={orgId}
                  loid={loid}
                />
              </div>
              <div className="col-span-12 lg:col-span-6 flex min-h-[450px]">
                <LazyLoadedTopLeadBreakdown
                  key={`top-lead-breakdown-${range}`}
                  title="Top Lead Breakdown"
                  range={range}
                  orgId={orgId}
                  loid={loid}
                />
              </div>
              <div className="col-span-12 flex">
                <LazyLoadedUTMParameters
                  key={`utm-parameters-${range}`}
                  title="UTM Parameters"
                  range={range}
                  orgId={orgId}
                  loid={loid}
                />
              </div>
              <div className="col-span-12 flex min-h-[450px]">
                <LazyLoadedLeadByStateAndCity
                  key={`lead-by-state-and-city-${range}`}
                  title="Leads by City and State"
                  range={range}
                  orgId={orgId}
                  loid={loid}
                />
              </div>
              <div className="col-span-12 flex min-h-[450px]">
                <LazyLoadedLeadByPropertyAndResidency
                  key={`lead-by-property-and-residency-${range}`}
                  title="Lead Stats"
                  range={range}
                  orgId={orgId}
                  loid={loid}
                />
              </div>
              <div className="col-span-12 lg:col-span-6 flex min-h-[450px]">
                <LazyLoadedLeadConversionFunnel
                  key={`lead-conversion-funnel-${range}`}
                  title="Lead Conversion Funnel"
                  range={range}
                  orgId={orgId}
                  loid={loid}
                />
              </div>
              <div className="col-span-12 lg:col-span-6 flex min-h-[450px]">
                <LazyLoadedLeadConversionProgress
                  key={`lead-conversion-progress-${range}`}
                  title="Lead Conversion Progress"
                  range={range}
                  orgId={orgId}
                  loid={loid}
                />
              </div>
              <div className="col-span-12 flex min-h-[450px]">
                <LazyLoadedRateTableUsageMetrics
                  key={`rate-table-usage-metrics-${range}`}
                  title="Rate Table Usage Metrics"
                  range={range}
                  orgId={orgId}
                  loid={loid}
                />
              </div>
              <div className="col-span-12 lg:col-span-6 flex min-h-[400px]">
                <LazyLoadedSubscriptionCount
                  key={`subscription-count-${range}`}
                  title="Subscription Count"
                  range={range}
                  orgId={orgId}
                  loid={loid}
                />
              </div>
              <div className="col-span-12 lg:col-span-6 flex min-h-[400px]">
                <LazyLoadedExitPopupConversionFunnel
                  key={`exit-popup-conversion-funnel-${range}`}
                  title="Exit Popup Conversion Funnel"
                  range={range}
                  orgId={orgId}
                  loid={loid}
                />
              </div>
              <div className="col-span-12 flex min-h-[510px]">
                <LazyLoadedEmailStats
                  key={`email-stats-${range}`}
                  title="Email Stats"
                  range={range}
                  orgId={orgId}
                  loid={loid}
                />
              </div>
              <div className="col-span-12 flex min-h-[510px]">
                <LazyLoadedLeadList
                  key={`lead-list-${range}`}
                  title="Lead List"
                  range={range}
                  orgId={orgId}
                  loid={loid}
                />
              </div>
            </Fragment>
          )}
        </div>
      </Fragment>
    </RequireRole>
  )
}

export default Stats
